import type { ReactNode } from 'react';
import { Stack, Text } from '@mantine/core';
import { Eyebrow } from '../../../components/ds';

/**
 * The auth surfaces this frame wraps. One string per screen, keyed to
 * {@link PANEL_COPY} — adding a new auth screen means adding a copy entry
 * here, not inventing new panel markup.
 */
export type AuthScreen =
  | 'sign-in'
  | 'sign-up'
  | 'reset-request'
  | 'reset-sent'
  | 'reset-form'
  | 'subscribe-owner'
  | 'subscribe-operator'
  | 'invite'
  | 'name';

interface PanelCopy {
  eyebrow: string;
  line: string;
  body: string;
}

/**
 * Per-screen marketing copy for the dark panel. Vendor-correct: a solo dealer
 * or a two-seat table, never "brick-and-mortar shops" or "locations" — vendor
 * has neither. Ported verbatim from `mtb-design-system/templates/vendor-auth/data.js`
 * `PANEL_COPY` (2026-08-05 conformance pass, vendor auth-surface issue).
 */
const PANEL_COPY: Record<AuthScreen, PanelCopy> = {
  'sign-in': {
    eyebrow: 'Vendor',
    line: 'The ledger for your table.',
    body: 'Sign back in to pick up cost, price and profit for every card that’s passed through your hands.',
  },
  'sign-up': {
    eyebrow: 'Vendor',
    line: 'Built for the dealer, not the storefront.',
    body: 'One seat or two. A spare room or a folding table at a show — track what a card cost you and what it sold for.',
  },
  'reset-request': {
    eyebrow: 'Vendor',
    line: 'Happens to everyone.',
    body: 'We’ll get you a link. Your inventory and ledger are exactly where you left them.',
  },
  'reset-sent': {
    eyebrow: 'Vendor',
    line: 'Check your email.',
    body: 'The link is good for one use and expires soon — no need to keep this tab open.',
  },
  'reset-form': {
    eyebrow: 'Vendor',
    line: 'One more step.',
    body: 'Set a new password and you’re straight back to your table.',
  },
  'subscribe-owner': {
    eyebrow: 'Vendor',
    line: 'Keep the table running.',
    body: 'One plan, two seats included. Add more of either as your table grows.',
  },
  'subscribe-operator': {
    eyebrow: 'Vendor',
    line: 'Almost there.',
    body: 'This account just needs its owner to start the plan back up.',
  },
  // vendor#59. The panel is deliberately IDENTICAL across every one of
  // InviteAcceptPage's five states — that fixed, generic copy is the
  // enumeration-safety mechanism (mtb-design-system/templates/vendor-team/
  // accept.js's `Panel`, notes.md §2): it never confirms an account exists
  // and never reveals who invited whom until the main content proves
  // identity (signed in AND the address matches). Do not vary this entry by
  // state — see InviteAcceptPage's docblock.
  invite: {
    eyebrow: 'Vendor',
    line: 'You’ve been invited.',
    body: 'Someone’s running a table on My TCG Binder and wants you on it. Sign in and we’ll show you who.',
  },
  // vendor#1436. No design covers this screen; it reuses the frame and the
  // simplest form pattern the auth surfaces already use.
  name: {
    eyebrow: 'Vendor',
    line: 'One more thing.',
    body: 'Your name is how a partner sees you on every card, note and split.',
  },
};

export interface AuthFrameProps {
  /** Selects the panel copy — see {@link PANEL_COPY}. */
  screen: AuthScreen;
  children: ReactNode;
}

/**
 * AuthFrame — the split-panel frame shared by every public auth surface
 * (`/sign-in`, `/sign-up`, `/reset-password`, `/subscribe`).
 *
 * Shell: (public) — rendered outside the desktop/kiosk shells, before
 * `RequireAuth` resolves (or, for `/subscribe`, immediately after). Never
 * imports a shell-scoped data hook.
 *
 * Non-obvious contracts:
 *   - The dark panel is a **fixed editorial surface** built from raw palette
 *     tokens (`--ink-950`, `--mint-400`) rather than the semantic tokens
 *     (`--bg`, `--fg`) the form side uses — palette tokens do not change
 *     under `[data-scheme]`, so the panel never follows the app's light/dark
 *     toggle. Only the form side (the `children`) is scheme-aware, via
 *     Mantine's own `var(--mantine-color-body)` defaults. This mirrors the
 *     consumer brand's own marketing surfaces, which are equally fixed-dark.
 *   - Responsive collapse is pure CSS (a `<style>` media query at 768px), not
 *     `useIsPhone` — that hook also requires `pointer: coarse` (phone-shell
 *     routing) and would make the panel disappear on a narrow *desktop*
 *     window, which is not what a split-panel auth screen wants. Both the
 *     panel and the form markup stay mounted at every width; the query only
 *     reflows them, so component tests (which don't evaluate media queries)
 *     see the full desktop markup.
 *   - Ported from `mtb-design-system/templates/vendor-auth/data.js`'s
 *     `AuthFrame` — match spacing/copy there before changing this file.
 */
export function AuthFrame({ screen, children }: AuthFrameProps) {
  const copy = PANEL_COPY[screen];

  return (
    <div className="auth-frame">
      <div className="auth-frame-panel">
        <div>
          <div className="auth-frame-logo-row">
            <div className="auth-frame-mark" aria-hidden="true" />
            <span className="auth-frame-wordmark">My TCG Binder</span>
          </div>
          {/* Weight/tracking come from `Eyebrow`'s own token defaults
              (`--eyebrow-font` / `--tracking-eyebrow`), not the template's
              locally hardcoded 600/0.18em — same precedent as
              `features/vault/components/HoldingThesis.tsx`: the token file is
              the canonical source, so the token wins over a template-local
              value. Only the panel's fixed (never-scheme-adaptive) colour is
              a real override — Mantine's `dimmed` would resolve differently
              under the app's light/dark toggle, and this panel never does. */}
          <Eyebrow c="rgba(245,245,247,0.55)">{copy.eyebrow}</Eyebrow>
          <div className="auth-frame-display-wrap">
            <div className="auth-frame-display">{copy.line}</div>
          </div>
          <p className="auth-frame-body">{copy.body}</p>
        </div>
        <div className="auth-frame-hairline" />
      </div>

      <div className="auth-frame-form-wrap">
        <div style={{ width: '100%', maxWidth: 360 }}>{children}</div>
      </div>

      <style>{`
        .auth-frame {
          display: flex;
          flex-direction: row;
          min-height: 100vh;
        }
        .auth-frame-panel {
          width: 340px;
          flex: 0 0 340px;
          padding: 48px 40px;
          background: var(--ink-950);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          min-height: 100%;
        }
        .auth-frame-logo-row {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 40px;
        }
        .auth-frame-mark {
          width: 20px;
          height: 20px;
          border-radius: 5px;
          background: var(--gradient-ink);
          flex-shrink: 0;
        }
        .auth-frame-wordmark {
          font-family: var(--font-mono);
          font-size: 11px;
          font-weight: 600;
          color: var(--ink-50);
          letter-spacing: 0.24em;
          text-transform: uppercase;
        }
        .auth-frame-display-wrap {
          margin-top: 6px;
        }
        .auth-frame-display {
          font-family: var(--font-serif);
          font-style: italic;
          color: var(--mint-400);
          font-size: 30px;
          line-height: 1.15;
          text-wrap: pretty;
        }
        .auth-frame-body {
          margin: 14px 0 0;
          font-size: 13.5px;
          line-height: 1.6;
          color: rgba(245, 245, 247, 0.62);
          text-wrap: pretty;
          max-width: 260px;
        }
        .auth-frame-hairline {
          height: 2px;
          width: 48px;
          background: var(--gradient-ink);
          border-radius: 2px;
        }
        .auth-frame-form-wrap {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
        }
        @media (max-width: 768px) {
          .auth-frame {
            flex-direction: column;
          }
          .auth-frame-panel {
            width: 100%;
            flex: none;
            padding: 20px 20px 16px;
            min-height: 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          }
          .auth-frame-logo-row {
            margin-bottom: 10px;
          }
          .auth-frame-display {
            font-size: 22px;
          }
          .auth-frame-body,
          .auth-frame-hairline {
            display: none;
          }
          .auth-frame-form-wrap {
            align-items: flex-start;
            padding: 28px 20px 40px;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Field — a labelled form row: a 12.5px/500 label above the input, matching
 * `mtb-design-system/templates/vendor-auth/pages.js`'s `Field()` helper
 * rather than Mantine's built-in `label` prop (whose type scale differs).
 *
 * Shell: (public). Purely presentational — no store or network access.
 */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={4}>
      <Text fz={12.5} fw={500} c="var(--fg)">
        {label}
      </Text>
      {children}
    </Stack>
  );
}
